import type { SurfaceName } from '@polyloop/sim';
import type { TrickEvent } from '../engine/trick-tracker.js';

export interface HudState {
  speedMs: number;
  surface: SurfaceName;
  isGrounded: boolean;
  airTimeMs: number;
  driftPower: number;
  tick: number;
  fps: number;
  score: number;
  events: readonly TrickEvent[];
}

export class Hud {
  private speedEl: HTMLDivElement;
  private speedNum: HTMLDivElement;
  private cornerEl: HTMLDivElement;
  private controlsEl: HTMLDivElement;
  private scoreEl: HTMLDivElement;
  private scoreNum: HTMLDivElement;
  private popupRoot: HTMLDivElement;
  private displayedScore = 0;
  private renderedEventIds = new WeakSet<TrickEvent>();

  constructor() {
    const root = document.getElementById('hud') ?? document.body;

    this.scoreEl = el('div', 'hud-score');
    const scoreLabel = el('div', 'unit');
    scoreLabel.textContent = 'SCORE';
    this.scoreNum = el('div', 'num');
    this.scoreNum.textContent = '0';
    this.scoreEl.appendChild(scoreLabel);
    this.scoreEl.appendChild(this.scoreNum);
    root.appendChild(this.scoreEl);

    this.speedEl = el('div', 'hud-speed');
    this.speedNum = el('div', 'num');
    this.speedNum.textContent = '0';
    const unit = el('div', 'unit');
    unit.textContent = 'KM/H';
    this.speedEl.appendChild(this.speedNum);
    this.speedEl.appendChild(unit);
    root.appendChild(this.speedEl);

    this.cornerEl = el('div', 'hud-corner');
    this.cornerEl.innerHTML = `
      <div><span class="k">FPS </span><span class="v" data-k="fps">--</span></div>
      <div><span class="k">TICK</span> <span class="v" data-k="tick">0</span></div>
      <div><span class="k">SURF</span> <span class="v" data-k="surf">road</span></div>
      <div><span class="k">GRND</span> <span class="v" data-k="grnd">no</span></div>
      <div><span class="k">AIR </span><span class="v" data-k="air">0ms</span></div>
      <div><span class="k">DRFT</span> <span class="v" data-k="drift">0</span></div>
    `;
    root.appendChild(this.cornerEl);

    this.popupRoot = el('div', 'hud-popups');
    root.appendChild(this.popupRoot);

    this.controlsEl = el('div', 'hud-controls');
    this.controlsEl.textContent =
      'WASD/arrows · SPACE = boost · SHIFT = drift · B = brake · R = reset';
    root.appendChild(this.controlsEl);

    injectHudStyles();
  }

  update(state: HudState): void {
    const kmh = Math.round(state.speedMs * 3.6);
    this.speedNum.textContent = String(kmh);

    // Animated score: ramp toward target so big swings feel chunky.
    if (this.displayedScore !== state.score) {
      const diff = state.score - this.displayedScore;
      this.displayedScore += Math.sign(diff) * Math.max(1, Math.ceil(Math.abs(diff) / 6));
      if (
        (diff > 0 && this.displayedScore > state.score) ||
        (diff < 0 && this.displayedScore < state.score)
      ) {
        this.displayedScore = state.score;
      }
      this.scoreNum.textContent = String(this.displayedScore);
    }

    this.setVal('fps', state.fps.toFixed(0));
    this.setVal('tick', String(state.tick));
    this.setVal('surf', state.surface);
    this.setVal('grnd', state.isGrounded ? 'yes' : 'no');
    this.setVal('air', state.isGrounded ? '0ms' : `${state.airTimeMs.toFixed(0)}ms`);
    this.setVal('drift', state.driftPower.toFixed(0));

    // Render any new popup events.
    for (const ev of state.events) {
      if (this.renderedEventIds.has(ev)) continue;
      this.renderedEventIds.add(ev);
      this.showPopup(ev);
    }
  }

  private showPopup(ev: TrickEvent): void {
    const p = el('div', `hud-popup hud-popup-${ev.kind}`);
    p.innerHTML = `<span class="lbl">${ev.label}</span><span class="pts">+${ev.points}</span>`;
    this.popupRoot.appendChild(p);
    // Trigger CSS transition.
    requestAnimationFrame(() => p.classList.add('is-on'));
    setTimeout(() => p.classList.add('is-leaving'), 900);
    setTimeout(() => p.remove(), 1500);
  }

  private setVal(k: string, v: string): void {
    const el = this.cornerEl.querySelector(`[data-k="${k}"]`);
    if (el) el.textContent = v;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}

let stylesInjected = false;
function injectHudStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
    .hud-score {
      position: absolute;
      left: 24px;
      bottom: 24px;
      pointer-events: none;
      text-align: left;
    }
    .hud-score .unit {
      font-size: 0.78rem;
      letter-spacing: 0.22em;
      opacity: 0.7;
      color: #00e0ff;
      margin-bottom: 2px;
      font-weight: 700;
    }
    .hud-score .num {
      font-size: clamp(2.4rem, 6vw, 4.4rem);
      font-weight: 800;
      line-height: 1;
      color: #ffffff;
      font-feature-settings: 'tnum' 1;
      text-shadow: 0 2px 16px rgba(0, 224, 255, 0.35);
    }

    .hud-popups {
      position: absolute;
      inset: 0;
      pointer-events: none;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding-top: 22vh;
      gap: 8px;
    }
    .hud-popup {
      display: flex;
      gap: 14px;
      align-items: baseline;
      padding: 8px 18px;
      border-radius: 999px;
      background: rgba(12, 13, 20, 0.75);
      backdrop-filter: blur(10px);
      color: #fff;
      font-weight: 800;
      letter-spacing: 0.14em;
      box-shadow: 0 6px 22px rgba(0, 0, 0, 0.4);
      transform: translateY(-12px) scale(0.94);
      opacity: 0;
      transition: transform 220ms cubic-bezier(0.15, 1.05, 0.6, 1), opacity 220ms ease;
    }
    .hud-popup.is-on { transform: translateY(0) scale(1); opacity: 1; }
    .hud-popup.is-leaving { transform: translateY(-22px) scale(0.92); opacity: 0; transition-duration: 600ms; }
    .hud-popup .lbl { font-size: 1.05rem; }
    .hud-popup .pts { font-size: 0.92rem; color: #00e0ff; font-feature-settings: 'tnum' 1; }

    .hud-popup-flip { background: linear-gradient(90deg, rgba(255, 62, 165, 0.85), rgba(255, 215, 92, 0.75)); }
    .hud-popup-flip .pts { color: #fff; }
    .hud-popup-long-jump { background: linear-gradient(90deg, rgba(0, 224, 255, 0.78), rgba(80, 160, 255, 0.65)); }
    .hud-popup-long-jump .pts { color: #fff; }
    .hud-popup-boost { background: linear-gradient(90deg, rgba(255, 215, 92, 0.85), rgba(255, 138, 58, 0.65)); }
    .hud-popup-boost .pts { color: #2a1c00; }
    .hud-popup-boost .lbl { color: #2a1c00; }
    .hud-popup-bump { background: rgba(255, 62, 165, 0.7); }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
}
